// src/components/ExplanationModal/ExplanationModal.jsx

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import StructuredExplanation from '../StructuredExplanation/StructuredExplanation';
import './ExplanationModal.css';
import ReactMarkdown from 'react-markdown';
import DOMPurify from 'dompurify';
import remarkGfm from 'remark-gfm';
import { IconButton, Tooltip, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GTranslateIcon from '@mui/icons-material/GTranslate';

const getLanguageName = (langCode) => {
    const lang = langCode.split('-')[0];
    const languageNames = new Intl.DisplayNames(['en'], { type: 'language' });
    return languageNames.of(lang);
};

const ExplanationModal = ({ 
    isOpen, 
    onClose, 
    title, 
    explanation, 
    isStructured, 
    score,
    taskKey,
    translatedExplanation,
    isCheckingCache,
    isTranslating,
    translationError,
    onTranslate
}) => {
  const { t, i18n } = useTranslation();
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  if (!isOpen) return null;

  const handleTranslateClick = () => {
    setIsTooltipOpen(false);
    onTranslate();
  };

  const explanationToShow = translatedExplanation || explanation;

  let cleanExplanation = explanationToShow;

  if (typeof explanationToShow === 'string') {
      // 1. Basic cleanup
      cleanExplanation = cleanExplanation.replace(/\\\*/g, ''); 

      // 2. AGGRESSIVE HEADER NORMALIZATION
      // Pattern: Start -> Bullet -> Header Text (no colons) -> Colon -> Rest of Line
      cleanExplanation = cleanExplanation.replace(
          /(^|\n)([\*\-]\s+)([^:\n]+):\s*(.*)/g,  // VETRA_AI_IGNORE_LINE
          (match, newline, bullet, headerContent, restOfLine) => {
              // 1. Clean the header: Remove existing asterisks, trim whitespace
              const cleanHeader = headerContent.replace(/\*/g, '').trim();
              
              // 2. Clean the body: Remove leading asterisks that might be left over (e.g. "** The body")
              let cleanBody = restOfLine.trim();
              // Remove up to 2 leading asterisks from body if they exist (fixing the "** Body" issue)
              cleanBody = cleanBody.replace(/^\*+\s*/, '');

              // 3. Reconstruct: Bullet + **Header:** + Space + Body
              return `${newline}${bullet}**${cleanHeader}:** ${cleanBody}`;
          }
      );
  }

  const modalContent = (
    <div
      className={`explanation-modal-overlay ${isOpen ? 'open' : ''}`}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="explanation-modal-title"
    >
      <div className="explanation-modal-content" onClick={(e) => e.stopPropagation()}>
        <IconButton
          aria-label={t('explanationModal.closeAriaLabel')}
          onClick={onClose}
          className="explanation-modal-close-button"
        >
          <CloseIcon />
        </IconButton>
        
        <div className="explanation-modal-header">
            {!isStructured && i18n.language !== 'en' && !translatedExplanation && !isCheckingCache && (
                <Tooltip
                    open={isTooltipOpen}
                    onOpen={() => setIsTooltipOpen(true)}
                    onClose={() => setIsTooltipOpen(false)}
                    title={t('resultDisplay.translateTooltip', { language: getLanguageName(i18n.language) })}
                >
                    <span style={{ pointerEvents: isTranslating ? 'none' : 'auto' }}>
                        <IconButton
                            size="small"
                            className="translate-explanation-button"
                            onClick={handleTranslateClick}
                            disabled={isTranslating}
                        >
                            {isTranslating ? <CircularProgress size={18} color="inherit"/> : <GTranslateIcon />}
                        </IconButton>
                    </span>
                </Tooltip>
            )}
            <h2 id="explanation-modal-title">{title}</h2>
        </div>

        <div className="explanation-modal-body">
          {isCheckingCache ? (
            <div className="translation-loader minimal"><CircularProgress size={24} color="primary"/></div>
          ) : isTranslating ? (
            <div className="translation-loader">
                <CircularProgress size={24} color="primary"/>
                <p>{t('resultDisplay.translatingMessage')}</p>
            </div>
          ) : (
            <>
              {isStructured ? (
                <StructuredExplanation data={cleanExplanation} score={score} taskKey={taskKey} />
              ) : (
                <div className="markdown-content">
                    <ReactMarkdown 
                        remarkPlugins={[remarkGfm]}
                        components={{
                            // Explicitly style strong tags
                            strong: ({node, ...props}) => <span style={{color: 'var(--primary-color) !important', fontWeight: '700'}} {...props} />,
                            // Catch any missed single-asterisk italics and style them as bold too
                            em: ({node, ...props}) => <span style={{color: 'var(--primary-color) !important', fontWeight: '700', fontStyle: 'normal'}} {...props} />
                        }}
                    >
                    {DOMPurify.sanitize(cleanExplanation)}
                    </ReactMarkdown>
                </div>
              )}
              {translationError && <p className="translation-error modal-error">{t('errors.translationFailed', { error: translationError })}</p>}
            </>
          )}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default ExplanationModal;